import { useEffect, useState } from 'react';

import type { FootprintView, TicketSuggestions } from '@nightshift/ui-types';
import {
  WORK_CATEGORIES,
  WORK_CATEGORY_LABELS,
  type WorkCategory,
} from '@nightshift/work-category';

import { Spinner } from '../../components/primitives.tsx';
import { api } from '../../live/api.ts';
import { useStore } from '../../live/context.tsx';
import { useTimeFormat } from '../../lib/display.ts';
import { observedTime } from '../../lib/format.ts';
import './footprint.css';

/**
 * What this ticket is expected to touch, and what it actually touched.
 *
 * Side by side rather than one replacing the other. The estimate is what a model believed
 * before the work started; the dependency scanner checks it against repository code. The
 * actual is the merge request diff. Keeping both visible is how a person notices that the
 * estimate was wrong, which is the only way the estimates ever get better.
 */
export function FootprintSection({
  suggestions,
  ticketKey,
  ticketVersion,
  now,
  onReloaded,
  showCategory = true,
}: {
  suggestions: TicketSuggestions;
  ticketKey: string;
  ticketVersion: number;
  now: number;
  onReloaded: () => Promise<void>;
  showCategory?: boolean;
}) {
  const store = useStore();
  const format = useTimeFormat();
  const { estimate, actual } = suggestions;
  const [selection, setSelection] = useState<WorkCategory | ''>(suggestions.category.override ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSelection(suggestions.category.override ?? '');
  }, [ticketKey, suggestions.category.override]);

  async function saveCategory(category: WorkCategory | null) {
    setBusy(true);
    try {
      await api.setCategory(ticketKey, category, ticketVersion);
      store.toast('good', category === null ? 'Category override cleared' : 'Category override saved');
      await onReloaded();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="fp">
      <h3 className="drawer-section-kicker">Footprint</h3>
      <p className="fp-coverage">Comparison: {suggestions.scanStatus.detail}</p>
      {showCategory ? <div className="fp-category" data-testid="footprint-category">
        <div className="fp-category-readout">
          <div>
            <span className="fp-heading">Effective category</span>
            <strong className="fp-category-value">
              {WORK_CATEGORY_LABELS[suggestions.category.effective]}
            </strong>
          </div>
          <span className={`fp-category-source is-${suggestions.category.source}`}>
            {suggestions.category.source === 'override'
              ? 'Manual override'
              : suggestions.category.source === 'estimate'
                ? 'Current estimate'
                : 'Fallback'}
          </span>
        </div>
        <div className={`fp-estimate-category${estimate?.historical ? ' is-historical' : ''}`}>
          <p className="fp-heading">
            {estimate?.historical ? 'Historical estimated category' : 'Estimated category'}
          </p>
          <p className="fp-category-estimate">
            {estimate?.category ? WORK_CATEGORY_LABELS[estimate.category] : 'Unclassified'}
          </p>
          {estimate?.categoryReason ? <p className="fp-summary">{estimate.categoryReason}</p> : null}
          <p className="fp-meta dim">{estimateProvenance(estimate, now, format)}</p>
        </div>
        <div className="fp-category-editor">
          <label htmlFor="ticket-category-override">Manual override</label>
          <div className="fp-category-actions">
            <select
              id="ticket-category-override"
              value={selection}
              disabled={busy}
              onChange={(event) => setSelection(event.target.value as WorkCategory | '')}
            >
              <option value="">Use current estimate</option>
              {WORK_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {WORK_CATEGORY_LABELS[category]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              disabled={busy || selection === (suggestions.category.override ?? '')}
              onClick={() => void saveCategory(selection || null)}
            >
              {busy ? <Spinner /> : null} Save category
            </button>
            {suggestions.category.override ? (
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                onClick={() => void saveCategory(null)}
              >
                Clear override
              </button>
            ) : null}
          </div>
        </div>
      </div> : null}
      {estimate || actual ? (
        <div className="fp-columns">
          <FootprintColumn
            heading={estimate?.historical ? 'Expected · historical' : 'Expected'}
            footprint={estimate}
            empty="Nothing has estimated this ticket yet."
            now={now}
          />
          <FootprintColumn
            heading="Actual"
            footprint={actual}
            empty="No merge request diff yet."
            now={now}
          />
        </div>
      ) : (
        <p className="fp-empty">{suggestions.scanStatus.detail}</p>
      )}
    </section>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  'estimate-raw': 'from the brief',
  'estimate-refined': 'from the refiner',
  'actual-mr': 'from the merge request',
  'actual-empty': 'merge request is empty',
};

function estimateProvenance(
  estimate: FootprintView | null,
  now: number,
  format: ReturnType<typeof useTimeFormat>,
): string {
  if (!estimate) return 'No footprint estimate has been recorded.';
  const origin =
    estimate.jobId !== null
      ? `footprint job ${estimate.jobId}`
      : estimate.attemptId !== null
        ? `attempt ${estimate.attemptId}`
        : SOURCE_LABEL[estimate.source] ?? estimate.source;
  const identity = estimate.briefHash
    ? ` · brief r${estimate.briefRevision ?? '?'} ${estimate.briefHash.slice(0, 8)}`
    : ' · legacy identity';
  return `${origin} · ${observedTime(estimate.createdAt, now, format)}${identity}`;
}

/**
 * Split a path so the file name survives a narrow column.
 *
 * A right-truncated path is close to useless here: `Assets/Scripts/Save/Sav...` could be the
 * format or the reader, and telling those two apart is the whole point of the list. The
 * directory is the part that can be dropped.
 */
function dirOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut === -1 ? '' : path.slice(0, cut + 1);
}

function nameOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut === -1 ? path : path.slice(cut + 1);
}

const DEGREE_ORDER: Record<string, number> = { heavy: 0, light: 1, touch: 2 };

function FootprintColumn({
  heading,
  footprint,
  empty,
  now,
}: {
  heading: string;
  footprint: FootprintView | null;
  empty: string;
  now: number;
}) {
  const format = useTimeFormat();
  if (!footprint || footprint.entries.length === 0) {
    return (
      <div className="fp-col">
        <h4 className="fp-heading">{heading}</h4>
        <p className="fp-empty">{footprint ? (SOURCE_LABEL[footprint.source] ?? empty) : empty}</p>
      </div>
    );
  }
  const entries = [...footprint.entries].sort(
    (a, b) => (DEGREE_ORDER[a.degree] ?? 3) - (DEGREE_ORDER[b.degree] ?? 3),
  );
  return (
    <div className="fp-col">
      <h4 className="fp-heading">
        {heading}
        <span className="dim num"> {footprint.entries.length}</span>
      </h4>
      <p className="fp-meta dim">
        {SOURCE_LABEL[footprint.source] ?? footprint.source}
        {' · '}
        {observedTime(footprint.createdAt, now, format)}
      </p>
      {footprint.summary ? <p className="fp-summary">{footprint.summary}</p> : null}
      <ul className="fp-list">
        {entries.map((entry) => (
          <li key={`${entry.path}-${entry.role}`} className={`fp-row is-${entry.degree}`}>
            <span className="fp-degree" aria-hidden="true" />
            <span
              className="mono fp-path"
              title={entry.why ? `${entry.path} -- ${entry.why}` : entry.path}
            >
              <span className="fp-dir">{dirOf(entry.path)}</span>
              <span className="fp-name">{nameOf(entry.path)}</span>
            </span>
            <span className="fp-tags dim">
              {entry.change}
              {entry.role === 'affected' ? ' · affected' : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
